import { createContext, useContext, useState, useCallback } from 'react';
import { useToast } from './ToastContext';
import { useAuth } from './AuthContext';
import { clearFavoritesCache } from '../lib/cache';

interface FavoritesContextType {
  refreshTrigger: number;
  triggerRefresh: () => void;
  isRateLimited: boolean;
}

const FavoritesContext = createContext<FavoritesContextType | undefined>(undefined);

export function useFavorites() {
  const context = useContext(FavoritesContext);
  if (!context) {
    throw new Error('useFavorites must be used within a FavoritesProvider');
  }
  return context;
}

const RATE_LIMIT_INTERVAL = 1000; // 1 second
const MAX_ACTIONS_PER_INTERVAL = 1;

export function FavoritesProvider({ children }: { children: React.ReactNode }) {
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [actionCount, setActionCount] = useState(0);
  const [isRateLimited, setIsRateLimited] = useState(false);
  const { showToast } = useToast();
  const { user } = useAuth();
  const [lastActionTimestamp, setLastActionTimestamp] = useState(0);

  const triggerRefresh = useCallback(() => {
    const now = Date.now();
    
    if (isRateLimited) {
      showToast('Please wait before performing another action', 'warning');
      return;
    }

    if (now - lastActionTimestamp < RATE_LIMIT_INTERVAL) {
      setIsRateLimited(true);
      showToast('Too many actions. Please wait a moment.', 'warning');
      setTimeout(() => {
        setIsRateLimited(false);
        setActionCount(0);
      }, RATE_LIMIT_INTERVAL);
      return;
    }

    setActionCount(prev => {
      const newCount = prev + 1;
      if (newCount >= MAX_ACTIONS_PER_INTERVAL) {
        setIsRateLimited(true);
        setTimeout(() => {
          setIsRateLimited(false);
          setActionCount(0);
        }, RATE_LIMIT_INTERVAL);
      }
      return newCount;
    });

    setLastActionTimestamp(now);
    if (user) {
      clearFavoritesCache(user.id);
    }
    setRefreshTrigger(prev => prev + 1);
  }, [isRateLimited, showToast, lastActionTimestamp, user]);

  return (
    <FavoritesContext.Provider value={{ refreshTrigger, triggerRefresh, isRateLimited }}>
      {children}
    </FavoritesContext.Provider>
  );
}